import React from "react";
import {
    Dialog,
    Box,
    Typography,
    Stack,
    Button,
    IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { useTheme } from "@mui/material/styles";
import { StatusCode } from "types/flagbag";

interface StatusOption {
    label: string;
    value: string;
    code: StatusCode;
    hidden?: boolean;
    disabled?: boolean;
}

interface Props {
    open: boolean;
    flagbagNum: string;
    currentStatus: StatusCode;
    statusOptions: StatusOption[];
    onClose: () => void;
    onConfirm: (newStatus: StatusCode) => void;
}

export const StatusUpdateModals: React.FC<Props> = ({
    open,
    flagbagNum,
    currentStatus,
    statusOptions,
    onClose,
    onConfirm,
}) => {
    const [selectedStatus, setSelectedStatus] = React.useState(currentStatus);
    const { isOnline } = useNetworkStatus();
    const theme = useTheme();
    // 当对话框打开或currentStatus变化时，重置选中状态
    React.useEffect(() => {
        if (open) {
            setSelectedStatus(currentStatus);
        }
    }, [currentStatus, open]);

    const handleConfirm = () => {
        // 检查选中的状态是否被禁用
        const selectedOption = statusOptions.find(opt => opt.code === selectedStatus);
        if (selectedOption?.disabled) {
            return; // 如果状态被禁用，不允许确认
        }
        onConfirm(selectedStatus);
        onClose();
    };

    return (
        <>
            {
                /*isOnline*/ true ? (
                    <Dialog
                        open={open}
                        onClose={onClose}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                borderRadius: "12px",
                                p: "18px",
                                gap: "6px"
                            }
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>
                                狀態修改
                            </Typography>
                            <IconButton onClick={onClose} size="small">
                                <Close />
                            </IconButton>
                        </Box>

                        <Box sx={{ bgcolor: "#F2F2F2", borderRadius: "6px", px: "12px", py: "6px" }}>
                            <Typography sx={{ fontSize: "14px", color: "#666" }}>
                                旗袋編號：{flagbagNum}
                            </Typography>
                        </Box>

                        <Stack spacing={1.5} sx={{ mb: "6px" }}>
                            {statusOptions.map((option) => {
                                if (option.hidden) return null;

                                // 获取当前选项对应的状态码
                                const optionStatusCode = option.code;
                                // 判断是否选中：比较状态码而不是字符串
                                const isSelected = selectedStatus === optionStatusCode;
                                // 判断是否禁用
                                const isDisabled = option.disabled || false;

                                return (
                                    <Box
                                        key={option.value}
                                        onClick={() => {
                                            if (!isDisabled) {
                                                setSelectedStatus(optionStatusCode);
                                            }
                                        }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            py: "4px",
                                            borderRadius: "8px",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            bgcolor: isSelected ? "#E3F2FD" : "transparent",
                                            opacity: isDisabled ? 0.5 : 1,
                                            "&:hover": {
                                                bgcolor: isDisabled 
                                                    ? "transparent" 
                                                    : (isSelected ? "#E3F2FD" : "#F5F5F5"),
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: "20px",
                                                height: "20px",
                                                borderRadius: "50%",
                                                border: `2px solid ${
                                                    isDisabled 
                                                        ? "#E0E0E0" 
                                                        : (isSelected ? "#2196F3" : "#BDBDBD")
                                                }`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {isSelected && !isDisabled && (
                                                <Box
                                                    sx={{
                                                        width: "10px",
                                                        height: "10px",
                                                        borderRadius: "50%",
                                                        bgcolor: "#2196F3",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography 
                                            sx={{ 
                                                fontSize: "14px", 
                                                color: isDisabled 
                                                    ? "#9E9E9E" 
                                                    : (isSelected ? "#2196F3" : "#000") 
                                            }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={
                                selectedStatus === currentStatus || 
                                statusOptions.find(opt => opt.code === selectedStatus)?.disabled === true
                            }
                            sx={{
                                backgroundColor: "#269FFF",
                                color: "#fff",
                                borderRadius: "8px",
                                py: "6px",
                                fontSize: "16px",
                                fontWeight: "600",
                                textTransform: "none",
                                "&:disabled": {
                                    backgroundColor: "#E0E0E0",
                                    color: "#9E9E9E",
                                },
                            }}
                        >
                            確認
                        </Button>
                    </Dialog>
                ) : (
                    <Dialog
                        open={open}
                        onClose={onClose}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                borderRadius: "12px",
                                p: "18px",
                            }
                        }}
                    >
                        <Box sx={{ fontSize: "14px", fontWeight: "500", display: "flex", flexDirection: "column", gap: "16px" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography sx={{ color: theme.palette.online_status.offline }}>
                                    無法修改該旗袋狀態
                                </Typography>
                                <Close onClick={onClose} />
                            </Box>
                            <Typography sx={{ color: "#000" }}>
                                離線模式下無法修改待處理旗袋的狀態，請恢復在線後重試。
                            </Typography>
                        </Box>
                    </Dialog>
                )
            }
        </>
    );
};
